import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle, FileDown, Loader2, Search, Users, Euro, RefreshCw,
  Phone, MapPin,
} from "lucide-react";

const fmtEur = (n) =>
  (Number(n) || 0).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function AdminClientiFuoriGiri() {
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentFilter, setAgentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/admin/sales-stats/clienti-fuori-giri");
      setData(r.data);
    } catch {
      toast.error("Errore caricamento elenco");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];
  const agents = useMemo(
    () =>
      [...new Set(items.map((i) => i.cod_agente).filter((v) => v != null))].sort(
        (a, b) => a - b
      ),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (agentFilter !== "all" && String(r.cod_agente) !== agentFilter)
        return false;
      if (!q) return true;
      return (
        r.ragione_sociale.toLowerCase().includes(q) ||
        r.codice_cli.toLowerCase().includes(q) ||
        (r.citta || "").toLowerCase().includes(q)
      );
    });
  }, [items, agentFilter, search]);

  const totEur = useMemo(
    () => filtered.reduce((s, r) => s + (r.eur || 0), 0),
    [filtered]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = agentFilter !== "all" ? { agente: agentFilter } : {};
      const r = await authAxios.get(
        "/admin/sales-stats/clienti-fuori-giri/export",
        { params, responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clienti_fuori_giri_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export pronto");
    } catch {
      toast.error("Errore export Excel");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <PageHeader title="Clienti fuori Giri" />

        {/* Info banner */}
        <Card className="bg-amber-500/5 border-amber-500/30 mb-4">
          <CardContent className="p-3 flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300">
              <p className="font-semibold text-amber-200 mb-1">
                Cosa vedi qui?
              </p>
              <p>
                Clienti che hanno acquistato (presenti nel file statistico
                vendite) ma <strong>non risultano in nessun giro</strong>.
                Possono essere: clienti banco, clienti nuovi non ancora
                schedati, o clienti da inserire in un giro esistente.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* KPI */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-1">
                <Users className="w-3.5 h-3.5" />
                Clienti
              </div>
              <div
                className="text-2xl font-bold text-white"
                data-testid="kpi-clienti-count"
              >
                {filtered.length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-1">
                <Euro className="w-3.5 h-3.5" />
                Fatturato
              </div>
              <div
                className="text-2xl font-bold text-emerald-400"
                data-testid="kpi-clienti-eur"
              >
                € {fmtEur(totEur)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/60 border-white/10 mb-4">
          <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Cerca per nome, codice o città…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-zinc-950 border-white/10"
                data-testid="search-input"
              />
            </div>
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger
                className="sm:w-44 bg-zinc-950 border-white/10"
                data-testid="agent-filter"
              >
                <SelectValue placeholder="Tutti gli agenti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a} value={String(a)}>
                    Agente {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={load}
              disabled={loading}
              className="border-white/10"
              data-testid="reload-btn"
              title="Ricarica"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || filtered.length === 0}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="export-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4 mr-2" />
              )}
              Excel
            </Button>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Caricamento…
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-zinc-900/60 border-white/10">
            <CardContent className="p-12 text-center text-zinc-500">
              Nessun cliente trovato.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="clienti-list">
            {filtered.map((r) => (
              <Card
                key={r.codice_cli}
                className="bg-zinc-900/60 border-white/10 hover:border-amber-500/30 transition-colors"
              >
                <CardContent className="p-3">
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white truncate">
                          {r.ragione_sociale}
                        </span>
                        {!r.in_anagrafica && (
                          <Badge
                            variant="outline"
                            className="border-red-500/40 text-red-300 text-[10px]"
                          >
                            non in anagrafica
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                        <span className="font-mono">{r.codice_cli}</span>
                        {r.citta && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {r.citta}
                            {r.provincia ? ` (${r.provincia})` : ""}
                          </span>
                        )}
                        {r.cod_agente != null && (
                          <span>Ag. {r.cod_agente}</span>
                        )}
                        {r.telefono && (
                          <a
                            href={`tel:${r.telefono}`}
                            className="flex items-center gap-1 text-amber-300 hover:underline"
                          >
                            <Phone className="w-3 h-3" />
                            {r.telefono}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-emerald-400 font-bold">
                        € {fmtEur(r.eur)}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {r.righe} righe
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 flex justify-between">
                    <span>Ultima vendita: {fmtDate(r.ultima_data)}</span>
                    <span>Qta tot: {fmtEur(r.qta)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
